import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { call } from "../../../firebase";
import { scheduleNext } from "../../../srs/fsrs";
import { createTypedAnswerProvider } from "../answerCapture";
import {
  completeStudySession,
  createStudySession,
  getDeck,
  getSessionCardsForDeck,
  getSrsStreak,
  markDailyStudyActivity,
  updateDeckCard,
  updateDeckCardMedia
} from "../firestore";
import { normalizeAnswer } from "../importParser";
import LatexText from "../components/LatexText";
import { uploadGeneratedCardAudioB64, uploadGeneratedCardImageB64 } from "../storage";
import CelebrationOverlay from "../../../components/CelebrationOverlay";
import StreakBanner from "../components/StreakBanner";

const DEFAULT_TARGET = 20;
const TTS_VOICES = ["alloy", "echo", "fable", "nova", "onyx", "shimmer", "verse"];

function evaluateCorrectness(card, answerText) {
  const normalizedAnswer = normalizeAnswer(answerText);
  if (!normalizedAnswer) return false;

  const accepted = [card.back, ...(card.accepted || [])]
    .map(normalizeAnswer)
    .filter(Boolean);

  if (!accepted.length) return false;
  if (card.strict) return accepted.includes(normalizedAnswer);
  return accepted.some(
    (candidate) =>
      normalizedAnswer.includes(candidate) || candidate.includes(normalizedAnswer)
  );
}

export default function StudyDeckPage() {
  const { deckId } = useParams();
  const [deck, setDeck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState("");
  const [targetCount, setTargetCount] = useState(DEFAULT_TARGET);

  const [sessionId, setSessionId] = useState("");
  const [sessionQueue, setSessionQueue] = useState([]);
  const [cardsById, setCardsById] = useState({});
  const [cursor, setCursor] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [typedAnswer, setTypedAnswer] = useState("");
  const [capturedAnswer, setCapturedAnswer] = useState("");
  const [done, setDone] = useState(false);
  const [sessionStats, setSessionStats] = useState(null);
  const [refineInstruction, setRefineInstruction] = useState("");
  const [refining, setRefining] = useState(false);
  const [refineError, setRefineError] = useState("");
  const [refineSuccess, setRefineSuccess] = useState("");
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState("");
  const [recordedMimeType, setRecordedMimeType] = useState("audio/webm");
  const [evaluating, setEvaluating] = useState(false);
  const [evalError, setEvalError] = useState("");
  const [evalResult, setEvalResult] = useState(null);
  const [applyingSuggestion, setApplyingSuggestion] = useState(false);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [mediaError, setMediaError] = useState("");
  const [mediaSuccess, setMediaSuccess] = useState("");
  const [audioGenText, setAudioGenText] = useState("");
  const [audioGenInstruction, setAudioGenInstruction] = useState("");
  const [audioGenVoice, setAudioGenVoice] = useState("alloy");
  const [imageGenInstruction, setImageGenInstruction] = useState("");
  const [showAudioGenText, setShowAudioGenText] = useState(false);
  const [streak, setStreak] = useState({ streakCount: 0, lastStudyDate: null });
  const [showStreakCelebration, setShowStreakCelebration] = useState(false);
  const [sessionSource, setSessionSource] = useState("due");

  const answerInputRef = useRef("");
  const reviewsRef = useRef([]);
  const cardUpdatesRef = useRef({});
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioPlayerRef = useRef(null);

  useEffect(() => {
    answerInputRef.current = typedAnswer;
  }, [typedAnswer]);

  useEffect(() => {
    let cancelled = false;

    async function loadDeck() {
      setLoading(true);
      setError("");
      try {
        const [data, streakData] = await Promise.all([
          getDeck(deckId),
          getSrsStreak(),
        ]);
        if (!cancelled) setDeck(data);
        if (!cancelled) setStreak(streakData);
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load deck.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadDeck();
    return () => {
      cancelled = true;
    };
  }, [deckId]);

  const currentCardId = sessionQueue[cursor];
  const currentCard = currentCardId ? cardsById[currentCardId] : null;

  const progress = useMemo(() => {
    if (!sessionQueue.length) return "0 / 0";
    return `${Math.min(cursor + 1, sessionQueue.length)} / ${sessionQueue.length}`;
  }, [cursor, sessionQueue.length]);

  useEffect(() => {
    setRefineInstruction("");
    setRefineError("");
    setRefineSuccess("");
    setEvalError("");
    setEvalResult(null);
    setMediaError("");
    setMediaSuccess("");
    setAudioGenText(currentCard ? String(currentCard.back || currentCard.front || "") : "");
    setAudioGenInstruction("");
    setAudioGenVoice("alloy");
    setImageGenInstruction("");
    setShowAudioGenText(false);
    setRecordedBlob(null);
    setRecordedMimeType("audio/webm");
    setRecordedUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
  }, [currentCard, currentCardId]);

  useEffect(() => {
    return () => {
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    };
  }, [recordedUrl]);

  const startSession = async () => {
    setStarting(true);
    setError("");

    try {
      const sessionPick = await getSessionCardsForDeck(deckId, Number(targetCount) || DEFAULT_TARGET);
      const dueCards = sessionPick.cards;
      setSessionSource(sessionPick.source || "due");
      if (!dueCards.length) {
        setSessionQueue([]);
        setCardsById({});
        setDone(true);
        setSessionStats({ reviewed: 0, lapses: 0 });
        return;
      }

      const nextCardsById = dueCards.reduce((acc, card) => {
        acc[card.id] = card;
        return acc;
      }, {});

      const nextQueue = dueCards.map((card) => card.id);
      const newSessionId = await createStudySession(deckId, nextQueue, Number(targetCount) || DEFAULT_TARGET);

      reviewsRef.current = [];
      cardUpdatesRef.current = {};
      setSessionId(newSessionId);
      setCardsById(nextCardsById);
      setSessionQueue(nextQueue);
      setCursor(0);
      setRevealed(false);
      setTypedAnswer("");
      setCapturedAnswer("");
      setDone(false);
      setSessionStats(null);
    } catch (err) {
      setError(err.message || "Failed to start session.");
    } finally {
      setStarting(false);
    }
  };

  const handleReveal = async () => {
    const getAnswer = createTypedAnswerProvider(() => answerInputRef.current);
    const result = await getAnswer();
    setCapturedAnswer(result.text);
    setRevealed(true);
  };

  const finishSession = async () => {
    if (!sessionId) return;

    setEnding(true);
    setError("");
    try {
      await completeStudySession({
        sessionId,
        deckId,
        cardUpdates: cardUpdatesRef.current,
        reviews: reviewsRef.current,
      });
      const lapses = reviewsRef.current.filter((r) => r.grade === "again").length;
      setDone(true);
      setSessionStats({ reviewed: reviewsRef.current.length, lapses });
    } catch (err) {
      setError(err.message || "Failed to complete session.");
    } finally {
      setEnding(false);
    }
  };

  const navigateCursor = (delta) => {
    if (!sessionQueue.length) return;
    const step = delta < 0 ? -1 : 1;
    let idx = cursor;
    let safety = 0;
    while (safety < sessionQueue.length) {
      idx += step;
      if (idx < 0 || idx >= sessionQueue.length) return;
      const nextId = sessionQueue[idx];
      if (nextId && cardsById[nextId]) {
        setCursor(idx);
        setRevealed(false);
        setTypedAnswer("");
        setCapturedAnswer("");
        return;
      }
      safety += 1;
    }
  };

  const handlePrevCard = () => {
    if (cursor <= 0) return;
    navigateCursor(-1);
  };

  const handleNextCard = () => {
    if (cursor >= sessionQueue.length - 1) return;
    navigateCursor(1);
  };

  const handleToggleAudioPlayback = async () => {
    const player = audioPlayerRef.current;
    if (!player) return;
    if (player.paused) {
      try {
        await player.play();
      } catch (_) {
        // user gesture/browser policy may block; ignore
      }
    } else {
      player.pause();
    }
  };

  const handleGrade = async (grade) => {
    if (!currentCard) return;

    const now = new Date();
    const priorSrs = cardUpdatesRef.current[currentCard.id] || currentCard.srs;
    const { nextSrs } = scheduleNext(priorSrs, grade, now, deck?.fsrsParams || {});

    cardUpdatesRef.current[currentCard.id] = nextSrs;
    try {
      const streakUpdate = await markDailyStudyActivity();
      setStreak((prev) => ({
        ...prev,
        streakCount: streakUpdate.streakCount,
        lastStudyDate: streakUpdate.lastStudyDate,
      }));
      if (streakUpdate.keptToday) {
        setShowStreakCelebration(true);
      }
    } catch (_) {
      // non-blocking streak update
    }

    const correct = evaluateCorrectness(currentCard, capturedAnswer);
    reviewsRef.current.push({
      cardId: currentCard.id,
      grade,
      reviewedAt: now,
      transcript: capturedAnswer,
      rawAnswer: capturedAnswer,
      correct,
    });

    if (grade === "again") {
      setSessionQueue((prev) => [...prev, currentCard.id]);
    }

    const nextCursor = cursor + 1;
    setCursor(nextCursor);
    setRevealed(false);
    setTypedAnswer("");
    setCapturedAnswer("");

    if (nextCursor >= sessionQueue.length + (grade === "again" ? 1 : 0)) {
      await finishSession();
    }
  };

  const handleRefineCard = async () => {
    if (!currentCard) return;
    if (!refineInstruction.trim()) {
      setRefineError("Enter a refinement instruction first.");
      return;
    }
    setRefining(true);
    setRefineError("");
    setRefineSuccess("");
    try {
      const res = await call("refineSrsCard", {
        cardId: currentCard.id,
        instruction: refineInstruction.trim(),
      });
      const updated = res?.data?.card || res?.data;
      if (!updated?.front || !updated?.back) throw new Error("No updated card returned.");
      setCardsById((prev) => ({
        ...prev,
        [currentCard.id]: {
          ...prev[currentCard.id],
          front: updated.front,
          back: updated.back,
          tags: updated.tags || [],
          strict: typeof updated.strict === "boolean" ? updated.strict : !!prev[currentCard.id]?.strict,
          accepted: updated.accepted || [],
        },
      }));
      setRefineSuccess("Card updated.");
    } catch (err) {
      const msg = err?.code === "functions/not-found"
        ? "refineSrsCard is not deployed. Deploy Firebase functions."
        : err.message || "Failed to refine card.";
      setRefineError(msg);
    } finally {
      setRefining(false);
    }
  };

  const startRecording = async () => {
    setEvalError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferredMimeTypes = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/wav",
      ];
      const selectedMimeType = preferredMimeTypes.find((m) => MediaRecorder.isTypeSupported(m));
      const recorder = selectedMimeType
        ? new MediaRecorder(stream, { mimeType: selectedMimeType })
        : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const mimeType = recorder.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setRecordedBlob(blob);
        setRecordedMimeType(mimeType);
        if (recordedUrl) URL.revokeObjectURL(recordedUrl);
        setRecordedUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((track) => track.stop());
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch (err) {
      setEvalError(err.message || "Could not start recording.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const clearRecording = () => {
    setRecordedBlob(null);
    setRecordedMimeType("audio/webm");
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedUrl("");
  };

  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || "");
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(new Error("Failed to read recorded audio."));
    reader.readAsDataURL(blob);
  });

  const handleGptEvaluate = async () => {
    if (!currentCard) return;
    const answerText = (revealed ? capturedAnswer : typedAnswer).trim();
    if (!answerText && !recordedBlob) {
      setEvalError("Type an answer or record audio before GPT evaluation.");
      return;
    }
    setEvaluating(true);
    setEvalError("");
    setEvalResult(null);
    try {
      const audioB64 = recordedBlob ? await blobToBase64(recordedBlob) : "";
      const res = await call("evaluateSrsResponse", {
        card: {
          front: currentCard.front,
          back: currentCard.back,
          accepted: currentCard.accepted || [],
          tags: currentCard.tags || [],
          strict: !!currentCard.strict,
        },
        answerText,
        audioB64,
        audioMimeType: recordedBlob ? recordedMimeType : "",
        languageHint: deck?.name || "",
      });
      setEvalResult(res?.data?.evaluation || null);
    } catch (err) {
      const msg = err?.code === "functions/not-found"
        ? "evaluateSrsResponse is not deployed. Deploy Firebase functions."
        : err.message || "GPT evaluation failed.";
      setEvalError(msg);
    } finally {
      setEvaluating(false);
    }
  };

  const applyUpdateSuggestion = async () => {
    if (!currentCard || !evalResult?.updateSuggestion?.shouldUpdateCard) return;
    const suggestion = evalResult.updateSuggestion;
    const nextBack = suggestion.proposedBack?.trim();
    if (!nextBack) return;
    setApplyingSuggestion(true);
    setEvalError("");
    try {
      await updateDeckCard(currentCard.id, {
        front: currentCard.front,
        back: nextBack,
        tags: currentCard.tags || [],
        strict: !!currentCard.strict,
        accepted: suggestion.proposedAccepted?.length ? suggestion.proposedAccepted : (currentCard.accepted || []),
      });
      setCardsById((prev) => ({
        ...prev,
        [currentCard.id]: {
          ...prev[currentCard.id],
          back: nextBack,
          accepted: suggestion.proposedAccepted?.length ? suggestion.proposedAccepted : (prev[currentCard.id]?.accepted || []),
        },
      }));
    } catch (err) {
      setEvalError(err.message || "Failed to apply card update suggestion.");
    } finally {
      setApplyingSuggestion(false);
    }
  };

  const applySuggestedGrade = async () => {
    if (!evalResult?.suggestedGrade) return;
    if (!revealed) {
      setCapturedAnswer(typedAnswer.trim());
      setRevealed(true);
      return;
    }
    await handleGrade(evalResult.suggestedGrade);
  };

  const patchLocalCardMedia = (cardId, patch) => {
    setCardsById((prev) => ({
      ...prev,
      [cardId]: {
        ...prev[cardId],
        ...patch,
      },
    }));
  };

  const handleGenerateStudyAudio = async () => {
    if (!currentCard) return;
    if (!audioGenText.trim()) {
      setMediaError("Audio text is required.");
      return;
    }
    setMediaBusy(true);
    setMediaError("");
    setMediaSuccess("");
    try {
      const res = await call("generateSrsCardAudio", {
        cardId: currentCard.id,
        text: audioGenText.trim(),
        instruction: audioGenInstruction.trim(),
        voice: audioGenVoice.trim() || "alloy",
      });
      const b64 = res?.data?.b64;
      if (!b64) throw new Error("No generated audio returned.");
      const uploaded = await uploadGeneratedCardAudioB64({ deckId, cardId: currentCard.id, b64 });
      await updateDeckCardMedia(currentCard.id, uploaded);
      patchLocalCardMedia(currentCard.id, uploaded);
      setMediaSuccess("Audio attached to this card.");
    } catch (err) {
      setMediaError(err.message || "Failed to generate audio.");
    } finally {
      setMediaBusy(false);
    }
  };

  const handleGenerateStudyImage = async () => {
    if (!currentCard) return;
    setMediaBusy(true);
    setMediaError("");
    setMediaSuccess("");
    try {
      const res = await call("generateSrsCardImage", {
        cardId: currentCard.id,
        instruction: imageGenInstruction.trim(),
      });
      const b64 = res?.data?.b64;
      if (!b64) throw new Error("No generated image returned.");
      const uploaded = await uploadGeneratedCardImageB64({ deckId, cardId: currentCard.id, b64 });
      await updateDeckCardMedia(currentCard.id, uploaded);
      patchLocalCardMedia(currentCard.id, uploaded);
      setMediaSuccess("Image attached to this card.");
    } catch (err) {
      setMediaError(err.message || "Failed to generate image.");
    } finally {
      setMediaBusy(false);
    }
  };

  useEffect(() => {
    if (!sessionId || done || !currentCard) return undefined;

    const isEditableTarget = (target) => {
      if (!target || !(target instanceof Element)) return false;
      const tag = target.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (target.getAttribute?.("contenteditable") === "true") return true;
      return false;
    };

    const onKeyDown = (event) => {
      if (isEditableTarget(event.target)) return;

      const key = event.key;
      if (!revealed && event.code === "Space") {
        event.preventDefault();
        handleReveal();
        return;
      }

      if ((key === "<" || key === ",") && cursor > 0) {
        event.preventDefault();
        handlePrevCard();
        return;
      }
      if ((key === ">" || key === ".") && cursor < sessionQueue.length - 1) {
        event.preventDefault();
        handleNextCard();
        return;
      }

      if (!revealed || ending) return;

      if (key === "j" || key === "J") {
        event.preventDefault();
        handleGrade("again");
      } else if (key === "k" || key === "K") {
        event.preventDefault();
        handleGrade("hard");
      } else if (key === "l" || key === "L") {
        event.preventDefault();
        handleGrade("good");
      } else if (key === ";") {
        event.preventDefault();
        handleGrade("easy");
      } else if (key === "p" || key === "P") {
        event.preventDefault();
        handleToggleAudioPlayback();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, done, currentCard, revealed, cursor, sessionQueue.length, ending, cardsById]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={3}>
        {showStreakCelebration ? (
          <CelebrationOverlay
            message="Streak kept"
            subtext={`${streak.streakCount} day streak`}
            onClose={() => setShowStreakCelebration(false)}
            duration={2600}
          />
        ) : null}

        <Button component={Link} to={`/srs/decks/${deckId}`} sx={{ alignSelf: "flex-start" }}>
          Back to Deck
        </Button>

        <StreakBanner streakCount={streak.streakCount} lastStudyDate={streak.lastStudyDate} />

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? <Typography>Loading deck...</Typography> : null}

        {!loading && deck ? (
          <Paper sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h4" fontWeight={800}>Study: {deck.name}</Typography>
              <Typography color="text.secondary">Progress: {progress}</Typography>
              {sessionId && sessionSource === "upcoming" ? (
                <Alert severity="info">No cards were due right now, so this session is using the next upcoming cards.</Alert>
              ) : null}

              {!sessionId && !done ? (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                  <TextField
                    label="Target cards"
                    type="number"
                    value={targetCount}
                    onChange={(e) => setTargetCount(e.target.value)}
                    inputProps={{ min: 1, max: 200 }}
                    sx={{ width: 160 }}
                  />
                  <Button variant="contained" onClick={startSession} disabled={starting}>
                    {starting ? "Starting..." : "Start Session"}
                  </Button>
                </Stack>
              ) : null}

              {done ? (
                <Alert severity="success">
                  Session complete. Reviewed {sessionStats?.reviewed ?? 0} cards. Again count: {sessionStats?.lapses ?? 0}.
                </Alert>
              ) : null}

              {sessionId && currentCard && !done ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="overline" color="text.secondary">Front</Typography>
                    <LatexText
                      component="div"
                      text={currentCard.front}
                      sx={{ fontSize: "1.25rem", fontWeight: 700, lineHeight: 1.4 }}
                    />
                    {currentCard.imageUrl ? (
                      <Box
                        component="img"
                        src={currentCard.imageUrl}
                        alt="card visual"
                        sx={{ mt: 1.5, maxWidth: "100%", maxHeight: 260, borderRadius: 1, border: "1px solid #ddd" }}
                      />
                    ) : null}
                    {currentCard.audioUrl ? (
                      <Box sx={{ mt: 1.5 }}>
                        <audio
                          ref={audioPlayerRef}
                          controls
                          preload="none"
                          src={currentCard.audioUrl}
                          style={{ width: "100%" }}
                        />
                      </Box>
                    ) : null}
                  </Box>

                  {!revealed ? (
                    <Stack spacing={2}>
                      <TextField
                        label="Your answer"
                        value={typedAnswer}
                        onChange={(e) => setTypedAnswer(e.target.value)}
                        multiline
                        minRows={2}
                        placeholder="Type your answer"
                      />
                      <Button variant="contained" onClick={handleReveal}>Reveal Answer</Button>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <Button variant="outlined" onClick={handlePrevCard} disabled={cursor <= 0}>Back (&lt;)</Button>
                        <Button
                          variant="outlined"
                          onClick={handleNextCard}
                          disabled={cursor >= sessionQueue.length - 1}
                        >
                          Forward (&gt;)
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      <Paper variant="outlined" sx={{ p: 2, backgroundColor: "#fafafa" }}>
                        <Typography variant="overline" color="text.secondary">Back</Typography>
                        <LatexText component="div" text={currentCard.back} sx={{ mb: 1 }} />
                        {(currentCard.accepted || []).length > 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            Accepted:
                            {" "}
                            <LatexText component="span" text={currentCard.accepted.join(", ")} />
                          </Typography>
                        ) : null}
                        <Typography variant="body2" color="text.secondary">
                          Your answer:
                          {" "}
                          <LatexText component="span" text={capturedAnswer || "(empty)"} />
                        </Typography>
                      </Paper>

                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <Button color="error" variant="contained" onClick={() => handleGrade("again")} disabled={ending}>Again</Button>
                        <Button color="warning" variant="contained" onClick={() => handleGrade("hard")} disabled={ending}>Hard</Button>
                        <Button color="success" variant="contained" onClick={() => handleGrade("good")} disabled={ending}>Good</Button>
                        <Button color="info" variant="contained" onClick={() => handleGrade("easy")} disabled={ending}>Easy</Button>
                        <Button variant="outlined" onClick={handlePrevCard} disabled={cursor <= 0}>Back (&lt;)</Button>
                        <Button
                          variant="outlined"
                          onClick={handleNextCard}
                          disabled={cursor >= sessionQueue.length - 1}
                        >
                          Forward (&gt;)
                        </Button>
                      </Stack>

                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2">Refine this card with GPT</Typography>
                          <TextField
                            label="Instruction"
                            placeholder="e.g., include the formal definition with LaTeX notation"
                            value={refineInstruction}
                            onChange={(e) => setRefineInstruction(e.target.value)}
                            multiline
                            minRows={2}
                          />
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                            <Button variant="outlined" onClick={handleRefineCard} disabled={refining}>
                              {refining ? "Updating..." : "Update Card with GPT"}
                            </Button>
                            {refineSuccess ? <Alert severity="success">{refineSuccess}</Alert> : null}
                          </Stack>
                          {refineError ? <Alert severity="error">{refineError}</Alert> : null}
                        </Stack>
                      </Paper>
                    </Stack>
                  )}

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.25}>
                      <Typography variant="subtitle2">Card Media Tools</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add GPT audio/image to this card directly from Study.
                      </Typography>
                      {mediaError ? <Alert severity="error">{mediaError}</Alert> : null}
                      {mediaSuccess ? <Alert severity="success">{mediaSuccess}</Alert> : null}

                      <Divider />
                      <Typography variant="body2" fontWeight={700}>GPT Audio</Typography>
                      <TextField
                        label="Text to speak"
                        value={audioGenText}
                        onChange={(e) => setAudioGenText(e.target.value)}
                        type={showAudioGenText ? "text" : "password"}
                        placeholder="e.g., Nei"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                edge="end"
                                aria-label={showAudioGenText ? "Hide audio text" : "Show audio text"}
                                onClick={() => setShowAudioGenText((prev) => !prev)}
                              >
                                {showAudioGenText ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <TextField
                          label="Pronunciation/style (optional)"
                          value={audioGenInstruction}
                          onChange={(e) => setAudioGenInstruction(e.target.value)}
                          fullWidth
                          placeholder="e.g., Norwegian Bokmal, slow learner pace"
                        />
                        <TextField
                          label="Voice"
                          value={audioGenVoice}
                          onChange={(e) => setAudioGenVoice(e.target.value)}
                          select
                          sx={{ width: { xs: "100%", sm: 180 } }}
                        >
                          {TTS_VOICES.map((voice) => (
                            <MenuItem key={voice} value={voice}>{voice}</MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                      <Button variant="outlined" onClick={handleGenerateStudyAudio} disabled={mediaBusy}>
                        {mediaBusy ? "Generating..." : "Generate GPT Audio"}
                      </Button>

                      <Divider />
                      <Typography variant="body2" fontWeight={700}>GPT Image</Typography>
                      <TextField
                        label="Image instruction (optional)"
                        value={imageGenInstruction}
                        onChange={(e) => setImageGenInstruction(e.target.value)}
                        placeholder="e.g., diagram of perennial lifecycle in a garden bed"
                      />
                      <Button variant="outlined" onClick={handleGenerateStudyImage} disabled={mediaBusy}>
                        {mediaBusy ? "Generating..." : "Generate GPT Image"}
                      </Button>
                    </Stack>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.25}>
                      <Typography variant="subtitle2">GPT Evaluate Answer (Optional)</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Keeps manual reveal and self-scoring intact. Use this only when you want AI feedback.
                      </Typography>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        {!recording ? (
                          <Button variant="outlined" onClick={startRecording}>Record Audio</Button>
                        ) : (
                          <Button color="warning" variant="contained" onClick={stopRecording}>Stop Recording</Button>
                        )}
                        <Button variant="outlined" onClick={clearRecording} disabled={!recordedBlob || recording}>
                          Clear Audio
                        </Button>
                        <Button variant="contained" onClick={handleGptEvaluate} disabled={evaluating || recording}>
                          {evaluating ? "Evaluating..." : "GPT Evaluate"}
                        </Button>
                      </Stack>
                      {recordedUrl ? (
                        <audio controls preload="none" src={recordedUrl} style={{ width: "100%" }} />
                      ) : null}
                      {evalError ? <Alert severity="error">{evalError}</Alert> : null}
                      {evalResult ? (
                        <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: "#fafafa" }}>
                          <Stack spacing={1}>
                            <Typography variant="body2">
                              Correctness: <strong>{evalResult.correctnessScore}/100</strong>
                              {typeof evalResult.pronunciationScore === "number"
                                ? ` | Pronunciation: ${evalResult.pronunciationScore}/100`
                                : ""}
                            </Typography>
                            <Typography variant="body2">
                              Suggested grade: <strong>{String(evalResult.suggestedGrade || "").toUpperCase()}</strong>
                            </Typography>
                            {evalResult.feedback ? (
                              <Typography variant="body2" color="text.secondary">{evalResult.feedback}</Typography>
                            ) : null}
                            {evalResult.transcriptionWarning ? (
                              <Alert severity="warning">{evalResult.transcriptionWarning}</Alert>
                            ) : null}
                            {evalResult.transcript ? (
                              <Typography variant="caption" color="text.secondary">
                                Transcript: {evalResult.transcript}
                              </Typography>
                            ) : null}
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                              <Button size="small" variant="outlined" onClick={applySuggestedGrade}>
                                {revealed ? "Use Suggested Grade" : "Reveal + Keep Suggested Grade"}
                              </Button>
                              {evalResult.updateSuggestion?.shouldUpdateCard ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={applyUpdateSuggestion}
                                  disabled={applyingSuggestion}
                                >
                                  {applyingSuggestion ? "Applying..." : "Apply Card Update Suggestion"}
                                </Button>
                              ) : null}
                            </Stack>
                            {evalResult.updateSuggestion?.shouldUpdateCard && evalResult.updateSuggestion?.reason ? (
                              <Typography variant="caption" color="text.secondary">
                                Suggested update reason: {evalResult.updateSuggestion.reason}
                              </Typography>
                            ) : null}
                          </Stack>
                        </Paper>
                      ) : null}
                    </Stack>
                  </Paper>
                </Stack>
              ) : null}
            </Stack>
          </Paper>
        ) : null}
      </Stack>
    </Container>
  );
}
